import { Box, Stack, Typography } from "@mui/material";
import { optionalClassName, prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { AquariusQuantity } from "@smartrr/shared/customerPortal/components/AquariusQuantity";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { TitleAndSearchBar } from "@smartrr/shared/customerPortal/components/TitleAndSearchBar";
import { ICustomerPortalTheme } from "@smartrr/shared/entities/CustomerPortalTheme";
import { QuickbuyAPI } from "@smartrr/shared/interfaces/quickbuy/api";
import { noawait } from "@smartrr/shared/utils/noawait";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import debounce from 'lodash/debounce';
import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";

import { QuickbuyProductSearch, QuickbuyStoreAccess } from "./store";
import { AquariusSelect } from "../../../components/AquariusSelect";
import { WIDTH_SIZES } from "../../../components/VariantImage";
import { useSmartrrTheme } from "../../../hooks/useSmartrrTheme";
import { useMobileQuery } from "../../../utils";
import { formatBasicPrice } from "../../../utils/Pricing";

const QuickbuyTitle = () => {
  const theme = useSmartrrTheme();
  const showSectionTitles = theme.modernThemeSettings?.showHide.showSectionTitles;

  if (!showSectionTitles) {
    return null;
  }

  return (
    <Typography variant="h2" className={prefixClassName("quickbuy-title")} data-testid="quickbuy-section-title">
      {typedLocalize("quickBuy.title")}
    </Typography>
  );
};

const QuickbuySearch = () => {
  const isMobile = useMobileQuery();
  const actions = QuickbuyProductSearch.access.useActions();

  const [searchText, setSearchText] = useState("");

  const handleChangeSearch = (s: string) => {
    setSearchText(s);
    actions.search.update(s);
  };

  return (
    <TitleAndSearchBar
      addClassName={prefixClassName("quickbuy-title-searchbar")}
      title={typedLocalize("quickBuy.header")}
      isMobile={isMobile}
      subscribedPurchasablesSearchText={searchText}
      updateSubscribedPurchasablesSearchText={handleChangeSearch}
      debounceTime={300}
      sx={{
        marginBottom: "15px",
      }}
    />
  );
};

const QuickbuyProductNameContainer = styled.div`
  &.smartrr-portal-quickbuy-clamp-line .MuiTypography-root {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    display: -webkit-box;
    overflow-y: hidden;
    text-overflow: ellipsis;
  }
`;

const QuickbuyProductName = ({ product }: { product: QuickbuyAPI.Product.Type }) => {
  const [showFull, setShowFull] = useState(false);

  return (
    <QuickbuyProductNameContainer
      className={optionalClassName({
        "quickbuy-product-name": true,
        "quickbuy-clamp-line": !showFull,
      })}
      onClick={() => setShowFull(!showFull)}
    >
      <Typography variant="body1">{product.name}</Typography>
    </QuickbuyProductNameContainer>
  );
};

const QuickbuyProductCTA = ({ product }: { product: QuickbuyAPI.Product.Type }) => {
  const selectedProduct = QuickbuyStoreAccess.useSelectedProduct();
  const actions = QuickbuyStoreAccess.useActions();
  const quantity = QuickbuyStoreAccess.useQuantity();

  return (
    <div className={prefixClassName("quickbuy-product-cta")}>
      {selectedProduct?.shopifyId === product.shopifyId ? (
        <AquariusQuantity value={quantity} onChange={q => actions.updateQuantity(q)} sx={{ margin: "0px" }} />
      ) : (
        <AquariusButton onClick={() => actions.updateProduct(product)} sx={{ marginTop: "0px", height: "30px" }}>
          {typedLocalize("quickBuy.buy")}
        </AquariusButton>
      )}
    </div>
  );
};

const QuickbuyProductPrice = ({ product }: { product: QuickbuyAPI.Product.Type }) => {
  const selectedVariant = QuickbuyStoreAccess.useSelectedVariant();

  const variant = product.variants[0];
  if (!variant) {
    return null;
  }

  const chosenVariant =
    selectedVariant && product.variants.find(v => v.shopifyId === selectedVariant.shopifyId)
      ? selectedVariant
      : variant;

  return (
    <div className={prefixClassName("quickbuy-product-price")}>
      <Typography variant="body1" align="left">
        {formatBasicPrice(
          {
            price: chosenVariant.price.amount,
            priceCurrency: chosenVariant.price.currency,
          },
          {
            removeTrailingZeros: true,
          }
        )}
      </Typography>
    </div>
  );
};

interface StyleProductProps {
  size: keyof typeof WIDTH_SIZES;
  aquariusTheme: ICustomerPortalTheme;
}

const StyledProduct = styled(Stack)<StyleProductProps>`
  & .smartrr-portal-quickbuy-product-image {
    overflow: hidden;
    border-radius: ${props => {
      return props.size === "small"
        ? `${props.aquariusTheme.modernSmallImageBorderRadius}px`
        : `${props.aquariusTheme.modernImageBorderRadius}px`;
    }};

    & img {
      width: 90px;
      height: 90px;
      object-fit: cover;
    }
  }

  & .smartrr-portal-quickbuy-product-name {
    width: 100%;
  }

  & .smartrr-portal-quickbuy-product-cta {
    width: 100%;
  }

  & .smartrr-portal-quickbuy-product-price {
    width: 100%;
  }

  & .smartrr-portal-quickbuy-product-image {
    height: 90px;
  }
`;

const QuickbuyProduct = ({ product }: { product: QuickbuyAPI.Product.Type }) => {
  const selectedVariant = QuickbuyStoreAccess.useSelectedVariant();
  const selectedProduct = QuickbuyStoreAccess.useSelectedProduct();

  const image = product.shopifyId === selectedProduct?.shopifyId ? selectedVariant?.image : product.image;

  const theme = useSmartrrTheme();
  return (
    <StyledProduct
      direction="column"
      spacing="7px"
      alignItems="center"
      sx={{ maxWidth: "90px" }}
      className={prefixClassName("quickbuy-product")}
      size="medium"
      aquariusTheme={theme}
    >
      <div className={prefixClassName("quickbuy-product-image")}>
        <img src={image ? image : product.image} alt={product.name} loading="lazy" />
      </div>
      <QuickbuyProductCTA product={product} />
      <QuickbuyProductName product={product} />
      <QuickbuyProductPrice product={product} />
    </StyledProduct>
  );
};

const DotSpinnerContainer = styled.div`
  display: none;
  justify-content: center;
  min-width: 60px;
  padding-bottom: 60px;
  padding-top: 60px;
  width: 60px;

  &.smartrr-portal-full-reload {
    min-width: 100%;
    width: 100%;
  }

  &.smartrr-portal-show-spinner {
    display: flex;
  }
`;

const QuickbuyProductLazyLoad = () => {
  const page = QuickbuyProductSearch.access.usePage();
  const actions = QuickbuyProductSearch.access.useActions();
  const isLoading = QuickbuyProductSearch.access.useLoading();
  const hasLoadedBefore = QuickbuyProductSearch.access.useHasLoadedBefore();
  const hasMorePages = QuickbuyProductSearch.access.useHasMorePages();

  const { ref, inView } = useInView({
    threshold: 0.2,
  });

  const debouncedUpdate = debounce((page: number) => {
    actions.page.update(page + 1);
  }, 100);

  useEffect(() => {
    if (inView && hasMorePages && !isLoading) {
      debouncedUpdate(page);
    }
    return () => debouncedUpdate.cancel();
  }, [inView, hasMorePages, isLoading]);

  return (
    <DotSpinnerContainer
      ref={ref}
      className={optionalClassName({
        "lazyload-helper": true,
        "full-reload": !hasLoadedBefore || isLoading === "search",
        "show-spinner": !!isLoading || hasMorePages || !hasLoadedBefore,
      })}
    >
      {isLoading ? <DotSpinner /> : null}
    </DotSpinnerContainer>
  );
};

const QuickbuyProductList = () => {
  const products = QuickbuyProductSearch.access.useData();

  return (
    <Stack
      direction="row"
      spacing="20px"
      sx={{ paddingBottom: "15px", overflowX: "auto", overflowY: "hidden", width: "100%" }}
      className={prefixClassName("quickbuy-product-container")}
    >
      {products.map((p, index) => (
        <QuickbuyProduct key={index} product={p} />
      ))}
      <QuickbuyProductLazyLoad />
    </Stack>
  );
};

const QuickbuyVariantSelection = () => {
  const product = QuickbuyStoreAccess.useSelectedProduct();
  const variant = QuickbuyStoreAccess.useSelectedVariant();
  const actions = QuickbuyStoreAccess.useActions();

  if (!product || !variant || product.variants.length === 1) {
    return null;
  }

  return (
    <AquariusSelect
      className={prefixClassName("quickbuy-variant-selection")}
      options={product.variants.map(v => ({ label: v.name, value: v.shopifyId }))}
      onChange={vid => {
        const variant = product.variants.find(v => v.shopifyId === vid);
        if (variant) {
          actions.updateVariant(variant);
        }
      }}
      value={variant.shopifyId}
    />
  );
};

const QuickbuyConfirmation = () => {
  const product = QuickbuyStoreAccess.useSelectedProduct();
  const actions = QuickbuyStoreAccess.useActions();

  if (!product) {
    return null;
  }

  return (
    <Stack sx={{ marginTop: "15px" }} className={prefixClassName("quickbuy-confirmation-container")}>
      <QuickbuyVariantSelection />
      <ConfirmCancelButtons
        addClassName={prefixClassName("quickbuy-confirm-cancel")}
        onConfirm={() => actions.buy()}
        onCancel={() => actions.updateProduct(null)}
        confirmTextOverride={"quickBuy.buy"}
      ></ConfirmCancelButtons>
    </Stack>
  );
};

const QuickBuyContent = () => {
  return (
    <AquariusCard className={prefixClassName("quickbuy-card")} sx={{ padding: "20px 40px !important" }}>
      <QuickbuySearch />
      <QuickbuyProductList />
      <QuickbuyConfirmation />
    </AquariusCard>
  );
};

export const QuickbuyHandler = () => {
  const actions = QuickbuyProductSearch.access.useActions();
  const products = QuickbuyProductSearch.access.useData();

  useEffect(() => {
    noawait(async () => {
      actions.dynamic.update({ perPage: 10 });
    });
  }, []);

  if (!products.length) {
    return <React.Fragment />;
  }

  return (
    <Box className={prefixClassName("quickbuy-section-wrapper")} marginTop={"40px"}>
      <span className="anchor" id="quickbuy" />
      <QuickbuyTitle />
      <QuickBuyContent />
    </Box>
  );
};
